// UPDATE_MARKER_HE_V5_QUERY_SUGGESTIONS
/**
 * Hebrew predictive search — native-look with query suggestions.
 *
 * Shopify's native predictive search 417s on the HE buyer locale AND returns no
 * Hebrew query suggestions, so we render our own dropdown (body-level portal so
 * the native close()/visibility can't hide it) reusing Dawn's predictive-search__*
 * markup + classes to look identical to the English dropdown:
 *   left  = "הצעות": synthesized search-term suggestions (from product titles) +
 *           matching collections — clickable, styled like native styled_text
 *   right = "מוצרים": thumbnail + Hebrew title + ILS price
 *   bottom = "חיפוש עבור …" button
 *
 * Data (parallel Storefront calls, @inContext HE/IL):
 *   search(query, types:[PRODUCT])  → Hebrew titles + ILS prices
 *   collections(query)              → Hebrew collection titles
 * Query suggestions are synthesized client-side because predictiveSearch.queries
 * is empty for Hebrew.
 */
(function () {
  if (typeof Shopify === 'undefined' || Shopify.locale !== 'he') return;

  const SF_TOKEN = '19668e37b1939728d9105e60f9cf6341';
  const SF_URL = '/api/2026-04/graphql.json';
  const DEBOUNCE_MS = 250;
  const MIN_QUERY = 2;
  const LIMIT = 8;
  const COLL_LIMIT = 4;
  const SUGG_LIMIT = 4;
  const PORTAL_ID = 'predictive-search-he-portal';

  function escapeHTML(s) {
    return String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
  function formatPrice(amount, currency) {
    if (amount == null) return '';
    const n = Number(amount);
    if (currency === 'ILS' || !currency) return '₪' + n.toLocaleString('he-IL', { maximumFractionDigits: 2 });
    return n.toLocaleString() + ' ' + currency;
  }
  function imgUrl(url, size) {
    return url ? url.replace(/(\.(?:jpg|jpeg|png|webp))(\?|$)/i, '_' + size + 'x$1$2') : '';
  }
  function debounce(fn, ms) {
    let t;
    return function () { const a = arguments, c = this; clearTimeout(t); t = setTimeout(() => fn.apply(c, a), ms); };
  }
  function sfFetch(query, variables) {
    return fetch(SF_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': SF_TOKEN, Accept: 'application/json' },
      body: JSON.stringify({ query, variables }),
    }).then((r) => r.json());
  }

  // ---- Data ----

  async function searchHE(query) {
    const [pr, cr] = await Promise.all([
      sfFetch(
        `query P($q: String!, $limit: Int!) @inContext(language: HE, country: IL) {
          search(query: $q, first: $limit, types: [PRODUCT], unavailableProducts: HIDE) {
            nodes { ... on Product {
              handle title
              featuredImage { url altText }
              priceRange { minVariantPrice { amount currencyCode } }
            } }
          }
        }`,
        { q: query, limit: LIMIT }
      ),
      sfFetch(
        `query C($q: String!, $limit: Int!) @inContext(language: HE) {
          collections(first: $limit, query: $q) { nodes { title handle } }
        }`,
        { q: query, limit: COLL_LIMIT }
      ),
    ]);
    if (pr.errors) throw new Error(pr.errors[0].message);
    const products = (pr.data && pr.data.search ? pr.data.search.nodes : []).filter((p) => p && p.handle);
    const collections = cr.data && cr.data.collections ? cr.data.collections.nodes : [];
    return { products, collections };
  }

  // Synthesize search-term suggestions from product titles (Shopify gives none for HE).
  function buildSuggestions(query, products, collections) {
    const seen = {};
    const out = [];
    const collTitles = {};
    collections.forEach((c) => { collTitles[(c.title || '').trim().toLowerCase()] = 1; });
    const norm = (s) => (s || '').trim().replace(/\s+/g, ' ');
    const add = (t) => {
      t = norm(t);
      const k = t.toLowerCase();
      if (!t || seen[k] || collTitles[k]) return; // skip dups + anything already a collection
      seen[k] = 1;
      out.push(t);
    };
    // first: two-word phrases from product titles (e.g. "שרשרת חמסה")
    products.forEach((p) => { const w = norm(p.title).split(' '); if (w.length >= 2) add(w[0] + ' ' + w[1]); });
    // then: bare category words (e.g. "שרשרת")
    products.forEach((p) => { add(norm(p.title).split(' ')[0]); });
    return out.slice(0, SUGG_LIMIT);
  }

  // ---- Portal + styles ----

  let portal = null;
  let stylesInjected = false;

  function injectStyles() {
    if (stylesInjected) return;
    stylesInjected = true;
    const style = document.createElement('style');
    style.id = 'ps-he-styles';
    style.textContent = `
      #${PORTAL_ID} {
        position: fixed; z-index: 99999;
        background: rgb(var(--color-background, 255 255 255));
        border: var(--popup-border-width, 0.1rem) solid rgba(var(--color-foreground, 18 18 18), var(--popup-border-opacity, 0.1));
        border-top: none;
        border-bottom-left-radius: var(--popup-corner-radius, 0);
        border-bottom-right-radius: var(--popup-corner-radius, 0);
        box-shadow: var(--popup-shadow-horizontal-offset, 0) var(--popup-shadow-vertical-offset, 0.4rem) var(--popup-shadow-blur-radius, 0.5rem) rgba(var(--color-shadow, 18 18 18), var(--popup-shadow-opacity, 0.12));
        max-height: 75vh; overflow-y: auto; -webkit-overflow-scrolling: touch;
        direction: rtl; text-align: right;
        font-family: var(--font-body-family, inherit);
        display: none;
      }
      #${PORTAL_ID}[data-open="true"] { display: block; }
      #${PORTAL_ID} .predictive-search__item { text-align: right; }
      #${PORTAL_ID} .predictive-search__item--term .icon-arrow,
      #${PORTAL_ID} .predictive-search__item--term .svg-wrapper { margin-left: 0; margin-right: calc(var(--font-heading-scale, 1) * 2rem); transform: scaleX(-1); }
      #${PORTAL_ID} .ps-he__state { padding: 2rem; text-align: center; color: rgba(var(--color-foreground, 18 18 18), 0.7); }
      #${PORTAL_ID} .ps-he__state--err { color: #a00; }
    `;
    document.head.appendChild(style);
  }

  function getPortal() {
    if (portal && document.body.contains(portal)) return portal;
    portal = document.createElement('div');
    portal.id = PORTAL_ID;
    portal.className = 'predictive-search predictive-search--header';
    portal.setAttribute('role', 'listbox');
    portal.setAttribute('aria-label', 'תוצאות חיפוש');
    document.body.appendChild(portal);
    return portal;
  }
  function positionPortal(input) {
    const p = getPortal();
    const r = input.getBoundingClientRect();
    const width = Math.min(Math.max(r.width, 480), window.innerWidth - 32);
    let right = Math.round(window.innerWidth - r.right);
    if (right + width > window.innerWidth - 8) right = Math.max(8, window.innerWidth - width - 8);
    p.style.top = `${Math.round(r.bottom + 1)}px`;
    p.style.right = `${right}px`;
    p.style.left = 'auto';
    p.style.width = `${Math.round(width)}px`;
  }
  function showPortal(input) { const p = getPortal(); positionPortal(input); p.setAttribute('data-open', 'true'); }
  function hidePortal() { if (portal) portal.setAttribute('data-open', 'false'); }

  // ---- Render (Dawn markup) ----

  // Wrap the typed query inside the suggestion in <mark> (Dawn styles non-mark bold, mark muted).
  function markQuery(text, query) {
    const i = text.toLowerCase().indexOf(query.toLowerCase());
    if (i < 0) return escapeHTML(text);
    return escapeHTML(text.slice(0, i)) + '<mark>' + escapeHTML(text.slice(i, i + query.length)) + '</mark>' + escapeHTML(text.slice(i + query.length));
  }

  function queryItem(text, query) {
    return `<li class="predictive-search__list-item" role="option" aria-selected="false">
      <a href="/search?q=${encodeURIComponent(text)}" class="predictive-search__item link link--text" tabindex="-1">
        <div class="predictive-search__item-content predictive-search__item-content--centered">
          <p class="predictive-search__item-heading predictive-search__item-query-result h5" aria-label="${escapeHTML(text)}">${markQuery(text, query)}</p>
        </div>
      </a>
    </li>`;
  }
  function collItem(c) {
    return `<li class="predictive-search__list-item" role="option" aria-selected="false">
      <a href="/collections/${escapeHTML(c.handle)}" class="predictive-search__item link link--text" tabindex="-1">
        <div class="predictive-search__item-content predictive-search__item-content--centered">
          <p class="predictive-search__item-heading h5">${escapeHTML(c.title)}</p>
        </div>
      </a>
    </li>`;
  }
  function productItem(p) {
    const img = p.featuredImage ? imgUrl(p.featuredImage.url, '150') : '';
    const alt = p.featuredImage && p.featuredImage.altText ? p.featuredImage.altText : p.title;
    const mp = p.priceRange && p.priceRange.minVariantPrice;
    const price = mp ? formatPrice(mp.amount, mp.currencyCode) : '';
    return `<li class="predictive-search__list-item" role="option" aria-selected="false">
      <a href="/products/${escapeHTML(p.handle)}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
        ${img ? `<img class="predictive-search__image" src="${escapeHTML(img)}" alt="${escapeHTML(alt)}" width="50" height="50" loading="lazy">` : ''}
        <div class="predictive-search__item-content">
          <p class="predictive-search__item-heading h5">${escapeHTML(p.title)}</p>
          ${price ? `<div class="price"><span class="price__regular"><span class="price-item price-item--regular">${price}</span></span></div>` : ''}
        </div>
      </a>
    </li>`;
  }

  function renderState(p, cls, msg) {
    p.innerHTML = `<div id="predictive-search-results" role="listbox"><div class="ps-he__state ${cls}">${escapeHTML(msg)}</div></div>`;
  }

  function renderResults(p, data, query) {
    const { products, collections } = data;
    const suggestions = buildSuggestions(query, products, collections);
    const hasSidebar = suggestions.length > 0 || collections.length > 0;

    if (!products.length && !hasSidebar) { renderState(p, '', `לא נמצאו תוצאות עבור "${query}"`); return; }

    const wrapMods =
      (products.length ? '' : ' predictive-search__results-groups-wrapper--no-products') +
      (hasSidebar ? '' : ' predictive-search__results-groups-wrapper--no-suggestions');

    const sidebar = hasSidebar
      ? `<div class="predictive-search__result-group">
          <div>
            <h2 class="predictive-search__heading text-body caption-with-letter-spacing">הצעות</h2>
            <ul class="predictive-search__results-list list-unstyled" role="group" aria-label="הצעות">
              ${suggestions.map((s) => queryItem(s, query)).join('')}
              ${collections.map(collItem).join('')}
            </ul>
          </div>
        </div>`
      : '';

    const productsCol = products.length
      ? `<div class="predictive-search__result-group">
          <div>
            <h2 class="predictive-search__heading text-body caption-with-letter-spacing">מוצרים</h2>
            <ul class="predictive-search__results-list list-unstyled" role="group" aria-label="מוצרים">
              ${products.map(productItem).join('')}
            </ul>
          </div>
        </div>`
      : '';

    p.innerHTML = `<div id="predictive-search-results" role="listbox">
      <div class="predictive-search__results-groups-wrapper${wrapMods}">
        ${sidebar}
        ${productsCol}
      </div>
      <div class="predictive-search__search-for-button">
        <a href="/search?q=${encodeURIComponent(query)}" class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" role="option" aria-selected="false">
          <span>חיפוש עבור "${escapeHTML(query)}"</span>
          <span class="svg-wrapper"><svg viewBox="0 0 14 10" fill="none" aria-hidden="true" class="icon icon-arrow" width="14" height="10"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.537.808a.5.5 0 01.817-.162l4 4a.5.5 0 010 .708l-4 4a.5.5 0 11-.708-.708L11.793 5.5H1a.5.5 0 010-1h10.793L8.646 1.354a.5.5 0 01-.109-.546z" fill="currentColor"></path></svg></span>
        </a>
      </div>
    </div>`;
  }

  // ---- Search flow ----

  function doSearch(input) {
    const p = getPortal();
    const query = input.value.trim();
    if (query.length < MIN_QUERY) { hidePortal(); return; }
    renderState(p, '', 'מחפש…');
    showPortal(input);
    searchHE(query)
      .then((data) => {
        if (input.value.trim() !== query) return;
        renderResults(p, data, query);
        showPortal(input);
      })
      .catch((err) => {
        console.error('[ps-he]', err);
        renderState(p, 'ps-he__state--err', 'תקלה זמנית בחיפוש. נסי שוב.');
        showPortal(input);
      });
  }

  // ---- Wire up ----

  function attachInput(input) {
    if (input.dataset.psHeAttached === '1') return;
    input.dataset.psHeAttached = '1';
    const debounced = debounce(() => doSearch(input), DEBOUNCE_MS);
    input.addEventListener('input', debounced);
    input.addEventListener('focus', () => { if (input.value.trim().length >= MIN_QUERY) doSearch(input); });
    input.addEventListener('keydown', (e) => { if (e.key === 'Escape') hidePortal(); });
    window.addEventListener('scroll', () => { if (portal && portal.getAttribute('data-open') === 'true') positionPortal(input); }, { passive: true });
    window.addEventListener('resize', () => { if (portal && portal.getAttribute('data-open') === 'true') positionPortal(input); });
    document.addEventListener('mousedown', (e) => {
      if (!portal) return;
      if (portal.contains(e.target) || e.target === input || input.contains(e.target)) return;
      hidePortal();
    }, true);
    const detailsEl = input.closest('details');
    if (detailsEl) detailsEl.addEventListener('toggle', () => { if (!detailsEl.open) hidePortal(); });
  }

  function findInput() {
    return document.getElementById('Search-In-Modal') || document.querySelector('input[name="q"][role="combobox"]');
  }
  function init() {
    injectStyles();
    const input = findInput();
    if (input) { attachInput(input); console.log('[ps-he] v5 query-suggestions initialized'); return true; }
    return false;
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  setTimeout(() => {
    const input = findInput();
    if (input && input.dataset.psHeAttached !== '1') { injectStyles(); attachInput(input); console.log('[ps-he] v5 query-suggestions initialized (deferred)'); }
  }, 500);
})();
